import { getFormProps, useForm } from '@conform-to/react';
import { getZodConstraint, parseWithZod } from '@conform-to/zod/v4';
import React, { useCallback, useState } from 'react';
import { type FetcherWithComponents } from 'react-router';

import type { AdminLoginActionResult } from '~/common/schemas/admin';
import { FormErrors } from '~/front/components/Form/FormErrors';
import UCFetcherForm from '~/front/components/Form/UCFetcherForm';
import { InputField } from '~/front/components/Input/InputField';
import RecaptchaCheckBox from '~/front/components/RecaptchaCheckBox';
import { Button } from '~/front/components_v2/ui/button';
import { loginSchema } from '~/front/models/admin/schemas';

const MemorizedRecaptcha = React.memo(RecaptchaCheckBox);

export function AdminLoginForm({
  recaptchaEnabled,
  intentName,
  fetcher,
}: {
  recaptchaEnabled: boolean;
  intentName: string;
  fetcher: FetcherWithComponents<AdminLoginActionResult>;
}) {
  const [recaptchaVerified, setRecaptchaVerified] = useState<boolean>(false);
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const [recaptchaReloadKey, setRecaptchaReloadKey] = useState(0);

  const handleVerifyRecaptcha = useCallback(async (recaptchaToken: string) => {
    setRecaptchaVerified(true);
    setRecaptchaToken(recaptchaToken);
  }, []);

  const handleRecaptchaExpired = useCallback(() => {
    setRecaptchaVerified(false);
  }, []);

  const [form, fields] = useForm({
    lastResult: fetcher.data?.parseResult,
    constraint: getZodConstraint(loginSchema(recaptchaEnabled)),
    onSubmit: () => {
      setRecaptchaVerified(false);
      setRecaptchaReloadKey(recaptchaReloadKey + 1);
    },
    shouldValidate: 'onBlur',
    shouldRevalidate: 'onInput',
    onValidate({ formData }) {
      return parseWithZod(formData, { schema: loginSchema(recaptchaEnabled) });
    },
  });

  const loginButtonDisabled = recaptchaEnabled ? !recaptchaVerified : false;

  return (
    <UCFetcherForm
      formProps={getFormProps(form)}
      method="post"
      fetcher={fetcher}
      className="flex flex-col gap-6 mt-9"
    >
      <InputField
        label="メールアドレス"
        type="email"
        id={fields.email.id}
        name={fields.email.name}
        errors={fields.email.errors}
      />
      <InputField
        label="パスワード"
        type="password"
        id={fields.password.id}
        name={fields.password.name}
        errors={fields.password.errors}
      />
      <input
        type="hidden"
        id={fields.recaptcha_token.id}
        name={fields.recaptcha_token.name}
        value={recaptchaToken}
      />
      {recaptchaEnabled && (
        <MemorizedRecaptcha
          handleVerifyRecaptcha={handleVerifyRecaptcha}
          handleRecaptchaExpired={handleRecaptchaExpired}
          action="login_admin"
          reloadKey={recaptchaReloadKey}
        />
      )}
      <div>
        <Button
          type="submit"
          name="intent"
          value={intentName}
          disabled={loginButtonDisabled}
          className="w-full"
        >
          ログイン
        </Button>
        {form.errors && <FormErrors errors={form.errors ?? []} />}
      </div>
    </UCFetcherForm>
  );
}
